import { Fragment, ReactNode, useEffect } from 'react'
import { useRouter } from 'next/router'

import appConfig from '~/config/app'
import ROUTES from '~/shared/constants/routes.const'
import authService from '~/services/auth.service'
import useCurrentUser from '~/hooks/useCurrentUser'
import { useGetMeQueryState } from '~/redux/apis/user-api'
import useGetFirstOnboardingPage from '~/onboarding/utils/useGetFirstOnboardingPage'
import useSubscriptionIsRequired from '~/subscription/hooks/useSubscriptionIsRequired'
import useCurrentFamily from '~/familyAccount/hooks/useCurrentFamily'
import useCheckSubscriptionAndBookingStatus from '~/auth/hooks/useSubscriptionAndBookingStatus'

interface GuestGuardProps {
  children: ReactNode
  redirect?: string
}

function GuestGuard({ children, redirect }: GuestGuardProps): React.ReactElement {
  const router = useRouter()
  const isSubscriptionApplicable = useSubscriptionIsRequired()
  const user = useCurrentUser()
  const { isError: isMeError } = useGetMeQueryState(undefined)
  const { isFamilyAdmin: isFamilyAdminFromProfiles, isFamilySettled } = useCurrentFamily()
  const { checkIfUserHasActiveTodPackage } = useCheckSubscriptionAndBookingStatus()
  const hasActiveTodPackage = user ? checkIfUserHasActiveTodPackage(user) : false
  // The profiles match against user.id, so they read "not admin" until /me has answered
  const isUserSettled = Boolean(user) || isMeError
  const isFamilyAdmin = isFamilyAdminFromProfiles || Boolean(user?.familyInfo?.isFamilyAdmin)

  // Check if the user was created within the last 5 minutes.
  const isNewUser = user?.createdAt && Date.now() - new Date(user.createdAt).getTime() < 5 * 60 * 1000
  const pathToFirstOnboardingPage = useGetFirstOnboardingPage()

  const pathnames: Record<string, string> = {
    'booking-for-self': ROUTES.BOOKING_FOR_SELF,
    'booking-for-other': ROUTES.BOOKING_FOR_OTHER,
    '/no/campaigns/dnb/saga-private-banking': ROUTES.DNB_CAMPAIGN_TOD,
  }
  // Booking and the DNB tutoring campaign 404 in the school platform, so those redirects resolve to the
  // dashboard instead of a hidden route.
  const bookingRedirect = appConfig.isSchoolPlatform ? ROUTES.DASHBOARD : undefined
  const redirectPathname = redirect
    ? redirect.startsWith('booking') || redirect.startsWith('/no/campaigns/dnb')
      ? bookingRedirect ?? pathnames[redirect]
      : redirect
    : undefined

  useEffect(() => {
    const auth = authService.isAuthenticated()
    const shouldRedirect = auth && router.isReady && isFamilySettled && isUserSettled

    if (shouldRedirect) {
      if (redirectPathname) {
        router.push(redirectPathname)
      } else if (isNewUser) {
        // /get-started/price, /student and /family all 404 in the school platform.
        if (isSubscriptionApplicable.isSubscriptionRequired && !appConfig.isSchoolPlatform) {
          router.push(ROUTES.GET_STARTED_PRICE)
        } else {
          router.push(pathToFirstOnboardingPage)
        }
      } else if (
        isSubscriptionApplicable.isSubscriptionRequired &&
        hasActiveTodPackage &&
        !appConfig.isSchoolPlatform
      ) {
        router.push(ROUTES.STUDENT_PANEL)
      } else if (isFamilyAdmin && !appConfig.isSchoolPlatform) {
        router.push(ROUTES.FAMILY_SWITCH_PROFILE)
      } else {
        router.push(ROUTES.DASHBOARD)
      }
    }
  }, [
    isNewUser,
    redirect,
    router,
    isSubscriptionApplicable.isSubscriptionRequired,
    redirectPathname,
    pathToFirstOnboardingPage,
    isFamilyAdmin,
    isFamilySettled,
    isUserSettled,
    hasActiveTodPackage,
  ])

  return <Fragment>{children}</Fragment>
}

export default GuestGuard
